import React, { useEffect, useState } from 'react';
import { generateId } from './utility/functions';
import { QuestRoute } from './api/QuestRoutes';
import HamQuest from './api/HamQuestGiver';
import { useGlobalState } from './stateProvider/StateProvider';
import Collapsible from './layout/Collapsable';
import Checkbox from './forms/Checkbox';
import LoadingEllipsis from './forms/LoadingEllipsis';
import Button from './forms/Button';
import Row from './layout/Row';

const LocModelDropdown = ({ getState, tabIndex, startDate, endDate, ...props }) => {
    const {
        state: {
            allLocations
        },
    } = useGlobalState();

    const [locModels, setLocModels] = useState(null);

    useEffect(() => {
        HamQuest.getQuery(QuestRoute.reports.models, { startDate, endDate })
            .then(revenue => {
                const state = Object.entries(revenue).map(([location, models]) => ({
                    label: allLocations.find(e => e.value === parseInt(location))?.label ?? 'Website',
                    value: parseInt(location),
                    key: 'locationId',
                    id: generateId(),
                    checked: true,
                    children: Object.entries(models).map(([model, terminals]) => ({
                        label: model,
                        value: model,
                        key: 'modelId',
                        id: generateId(),
                        type: terminals.modelType,
                        checked: true,
                        children: terminals.terminalIds.map(terminal => ({
                            label: terminal,
                            value: terminal,
                            key: 'terminalId',
                            id: generateId(),
                            checked: true,
                        })),
                    })),
                }));
                updateState(state);
            })
    }, [startDate, endDate]);

    const updateState = (newState) => {
        setLocModels(newState);
        getState({
            query: buildQuery(newState),
            locations: getChecked(newState).map(e => e.label),
        })
    }

    const getChecked = (data) => {
        return data.reduce((acc, item) => {
            if (item.checked) {
                let newItem = { ...item };
                if (newItem.children) {
                    newItem.children = getChecked(newItem.children);
                }
                acc.push(newItem);
            }
            return acc;
        }, []);
    };

    const updateById = (data, queryId, newData) => {
        data.forEach(item => {
            if (item.id === queryId) {
                Object.assign(item, newData);
            } else if (item.children) {
                updateById(item.children, queryId, newData);
            }
        });
    }

    const updateParentCheckedState = (data, queryId) => {
        return data.some(item => {
            if (item.id === queryId) {
                if (item.parent) {
                    item.parent.checked = item.parent.children.some(c => c.checked);
                }
                return true;
            }

            if (item.children) {
                const found = updateParentCheckedState(
                    item.children.map(c => ({ ...c, parent: item })),
                    queryId
                );
                if (found && item.parent) {
                    item.parent.checked = item.parent.children.some(c => c.checked);
                }
                return found;
            }

            return false;
        });
    };

    const handleCheckboxChange = (item, checked) => {
        let newState = structuredClone(locModels);
        const updateChildrenChecked = (e) => {
            e.forEach((child) => {
                updateById(newState, child.id, { ...child, checked });
                if (child.children) {
                    updateChildrenChecked(child.children);
                }
            });
        };

        updateById(newState, item.id, { ...item, checked });
        if (item.children) {
            updateChildrenChecked(item.children);
        }
        updateParentCheckedState(newState, item.id);
        updateState(newState);
    };

    const updateChecked = (checked) => {
        let newState = structuredClone(locModels);
        const updateChildrenChecked = (e) => {
            e.forEach((child) => {
                updateById(newState, child.id, { ...child, checked });
                if (child.children) {
                    updateChildrenChecked(child.children);
                }
            });
        };

        newState.forEach(e => {
            updateById(newState, e.id, { ...e, checked });
            if (e.children) {
                updateChildrenChecked(e.children);
            }
        })

        updateState(newState);
    };

    const buildQuery = (state) => {
        const postBody = [];
        let postData = getChecked(state);
        postData.forEach(loc => {
            loc.children.forEach(model => {
                model.children.forEach(term => {
                    postBody.push({
                        locationId: loc.value,
                        model: model.value,
                        terminalId: term.value
                    })
                })
            })
        })
        return postBody;
    }

    return locModels ? locModels.length > 0 ? (
        <>
            <Collapsible label={'Locations'} startState={false}>
                {locModels.map((loc, i) => (
                    <Collapsible
                        key={`LOC${i}`}
                        label={
                            <Checkbox
                                label={loc.label}
                                value={loc.key}
                                style={{ fontSize: '0.5em', }}
                                inputClick={event => handleCheckboxChange(loc, event.checked)}
                                checked={loc.checked}
                                standalone
                                readOnly
                            />
                        }>
                        {loc.children.filter(e => 
                                tabIndex === undefined ? true :
                                (tabIndex === 2 ? e.type === 'Timer' ? e : false : e.type === 'Teller' ? e : false)
                            ).map((model, i) => (
                            <Collapsible
                                key={`MODEL${i}`}
                                label={
                                    <Checkbox
                                        label={model.label}
                                        value={model.key}
                                        style={{ fontSize: '0.5em', }}
                                        inputClick={event => handleCheckboxChange(model, event.checked)}
                                        checked={model.checked}
                                        standalone
                                        readOnly
                                    />
                                }>
                                {model.children.map((term, i) => (
                                    <Checkbox
                                        key={`TERM${i}`}
                                        label={term.label}
                                        value={term.key}
                                        style={{ fontSize: '0.5em', }}
                                        inputClick={event => handleCheckboxChange(term, event.checked)}
                                        checked={term.checked}
                                        standalone
                                        readOnly
                                    />
                                ))}
                            </Collapsible>
                        ))}
                    </Collapsible>
                ))}
            </Collapsible>
            <Row justify='space-around'>
                <Button
                    style={{ flex: 1 }}
                    onClick={() => {
                        updateChecked(true)
                    }}
                >All</Button>
                <Button
                    style={{ flex: 1 }}
                    onClick={() => {
                        updateChecked(false)
                    }}
                >Clear</Button>
            </Row>
        </>
    )
        : <span>No Data within range.</span>
        : <span>Loading Locations<LoadingEllipsis /></span>
}

export default LocModelDropdown;